/**
 * Centralized environment-driven config.
 *
 * One read of `import.meta.env` happens here. Everything else
 * imports the typed `config` export, so a change to the env-var
 * vocabulary touches one file.
 *
 * Vite injects three modes:
 *   - `development` (npm run dev)
 *   - `staging`     (vite build --mode staging)
 *   - `production`  (vite build)
 *
 * Each mode loads the matching `.env.<mode>` file. See `.env.example`
 * for the full vocabulary.
 */

const env = import.meta.env

function asBool(value: string | undefined, fallback = false): boolean {
  if (value == null || value === '') return fallback
  return value !== 'false' && value !== '0'
}

function asInt(value: string | undefined, fallback: number): number {
  const n = parseInt(value ?? '', 10)
  return Number.isFinite(n) ? n : fallback
}

function asList(value: string | undefined): string[] {
  if (!value) return []
  return String(value)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

export const config = Object.freeze({
  /** 'development' | 'staging' | 'production' */
  mode: env.MODE ?? 'development',
  isDev: env.MODE === 'development',
  isStaging: env.MODE === 'staging',
  isProd: env.MODE === 'production',

  /** When true the service layer resolves from mock fixtures. */
  useMocks: asBool(env.VITE_USE_MOCKS, true),

  /**
   * Domains switched to the real API even while `useMocks` is on.
   *
   * The wave plan wires the SPA to the backend one area at a time
   * (W3.M4 auth, W3.M6 clients, W3.M7 brands), so a single global
   * mock switch is the wrong granularity: flipping it would point
   * every service at endpoints that do not exist yet. Read through
   * `isLive(domain)`, never directly.
   */
  liveDomains: new Set(asList(env.VITE_LIVE_DOMAINS)),

  /** Mock simulator latency preset: 'fast' | 'normal' | 'slow' | 'random' */
  mockTiming: env.VITE_MOCK_TIMING || 'normal',
  /** Random-error injection rate (0..1). Default 0 = never inject. */
  mockErrorRate: parseFloat(env.VITE_MOCK_ERROR_RATE ?? '') || 0,

  api: {
    baseUrl: env.VITE_API_BASE_URL || '/api',
    timeoutMs: asInt(env.VITE_API_TIMEOUT_MS, 20_000),
    /** Default headers applied to every outbound request. */
    headers: {
      Accept: 'application/json',
    },
  },

  auth: {
    /** 'localStorage' | 'sessionStorage' | 'memory' */
    storage: env.VITE_AUTH_STORAGE || 'localStorage',
    refreshLeeway: asInt(env.VITE_AUTH_REFRESH_LEEWAY_SEC, 60),
    accessKey: 'lx.auth.access',
    refreshKey: 'lx.auth.refresh',
  },

  realtime: {
    /** ws:// or wss:// URL for push channel; '' disables it. */
    url: env.VITE_WS_URL || '',
  },

  observability: {
    sentryDsn: env.VITE_SENTRY_DSN || '',
    analyticsKey: env.VITE_ANALYTICS_KEY || '',
  },

  /**
   * Feature flags read at build time. Use `flags.has('name')`.
   *
   *   if (config.flags.has('kanban-view')) { … }
   */
  flags: new Set(asList(env.VITE_FLAGS)),
})

/**
 * Does this domain talk to the real API?
 *
 * True when mocks are off entirely, or when this specific domain has
 * been switched over ahead of the rest.
 */
export function isLive(domain: string): boolean {
  return !config.useMocks || config.liveDomains.has(domain)
}

export default config
